import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { permitApi, plantApi, ApiError } from '../../lib/api';
import type { PermitType, PlantNode } from '../../lib/types';
import { Spinner, ErrorBanner, DisclosureBanner } from '../../components/Misc';
import { titleCase } from '../../lib/format';

const PERMIT_TYPES: PermitType[] = ['HOT_WORK', 'COLD_WORK', 'CONFINED_SPACE', 'LINE_BREAKING', 'ELECTRICAL_ISOLATION', 'RADIOGRAPHY', 'CRANE_LIFT', 'EXCAVATION'];

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function NewPermitPage() {
  const { currentPlant } = usePlant();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<PlantNode[] | null>(null);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = useMemo(() => new Date(), []);
  const [form, setForm] = useState({
    permitNumber: '',
    permitType: 'HOT_WORK' as PermitType,
    scopeText: '',
    performingCompany: '',
    primaryNodeId: '',
    plannedStart: toLocalInput(now),
    plannedEnd: toLocalInput(new Date(now.getTime() + 4 * 60 * 60 * 1000)),
    createLocation: false,
    locationName: '',
    locationX: '',
    locationY: '',
  });

  useEffect(() => {
    if (!currentPlant) return;
    setLoadingNodes(true);
    plantApi
      .topology(currentPlant.id)
      .then((res) => setNodes(res.nodes))
      .finally(() => setLoadingNodes(false));
  }, [currentPlant?.id]);

  if (!currentPlant) return <Spinner />;

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentPlant) return;
    setSubmitting(true);
    setError(null);
    try {
      let workLocationId: string | undefined;
      if (form.createLocation && form.locationName && form.locationX !== '' && form.locationY !== '') {
        const loc = await plantApi.createWorkLocation(currentPlant.id, {
          name: form.locationName,
          x: Number(form.locationX),
          y: Number(form.locationY),
        });
        workLocationId = loc.workLocationId;
      }

      const { permitId } = await permitApi.create(currentPlant.id, {
        permitNumber: form.permitNumber.trim(),
        permitType: form.permitType,
        scopeText: form.scopeText.trim(),
        performingCompany: form.performingCompany.trim() || undefined,
        primaryNodeId: form.primaryNodeId || undefined,
        workLocationId,
        plannedStart: new Date(form.plannedStart).toISOString(),
        plannedEnd: new Date(form.plannedEnd).toISOString(),
        taggedNodeIds: form.primaryNodeId ? [form.primaryNodeId] : undefined,
      });
      navigate(`/app/permits/${permitId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the permit. Please check the form and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="stack gap-5" style={{ maxWidth: 720 }}>
      <Link to="/app/permits" className="row gap-1 body-small text-tertiary" style={{ width: 'fit-content' }}>
        <ArrowLeft size={14} /> Back to permits
      </Link>
      <div className="stack gap-1">
        <h1 className="h2-section">New permit</h1>
        <p className="body-text">{currentPlant.name} · {currentPlant.code}. The conflict engine evaluates on demand after creation — nothing is auto-approved.</p>
      </div>

      {error && <ErrorBanner message={error} />}

      <form className="card stack gap-4" onSubmit={handleSubmit}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="permitNumber">Permit number</label>
            <input id="permitNumber" required placeholder="PTW-3001" value={form.permitNumber} onChange={(e) => set('permitNumber', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="permitType">Permit type</label>
            <select id="permitType" value={form.permitType} onChange={(e) => set('permitType', e.target.value as PermitType)}>
              {PERMIT_TYPES.map((t) => (
                <option key={t} value={t}>{titleCase(t)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="scopeText">Scope of work</label>
          <textarea id="scopeText" required rows={4} placeholder="Describe the work, including any equipment tags you know (e.g. V-102, L-204)…" value={form.scopeText} onChange={(e) => set('scopeText', e.target.value)} />
          <DisclosureBanner>
            <Sparkles size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} />
            <strong>AI tag suggestions</strong> become available once the permit is created — open it and
            use "Suggest equipment tags with AI" to get parsing help. Suggestions are never
            auto-applied: you confirm every tag manually, and the conflict engine only ever reasons
            about confirmed, registry-resolved equipment — never raw model output.
          </DisclosureBanner>
        </div>

        <div className="field">
          <label htmlFor="performingCompany">Performing company</label>
          <input id="performingCompany" placeholder="Delta Mechanical Contractors" value={form.performingCompany} onChange={(e) => set('performingCompany', e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="primaryNodeId">Primary equipment (optional but recommended)</label>
          {loadingNodes ? (
            <Spinner label="Loading plant topology…" />
          ) : (
            <select id="primaryNodeId" value={form.primaryNodeId} onChange={(e) => set('primaryNodeId', e.target.value)}>
              <option value="">— None selected —</option>
              {nodes?.map((n) => (
                <option key={n.id} value={n.id}>{n.tagNumber} — {n.name}</option>
              ))}
            </select>
          )}
          <span className="field-hint">Selecting a node lets the spatial and isolation rules reason about this permit precisely.</span>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="plannedStart">Planned start</label>
            <input id="plannedStart" type="datetime-local" required value={form.plannedStart} onChange={(e) => set('plannedStart', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="plannedEnd">Planned end</label>
            <input id="plannedEnd" type="datetime-local" required value={form.plannedEnd} onChange={(e) => set('plannedEnd', e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label className="row gap-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={form.createLocation} onChange={(e) => set('createLocation', e.target.checked)} />
            Add a specific work location (needed for hot-work spatial-clearance checks)
          </label>
        </div>

        {form.createLocation && (
          <div className="grid-3">
            <div className="field">
              <label htmlFor="locationName">Location name</label>
              <input id="locationName" placeholder="TK-01 Manway Platform" value={form.locationName} onChange={(e) => set('locationName', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="locationX">X (m)</label>
              <input id="locationX" type="number" step="0.1" value={form.locationX} onChange={(e) => set('locationX', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="locationY">Y (m)</label>
              <input id="locationY" type="number" step="0.1" value={form.locationY} onChange={(e) => set('locationY', e.target.value)} />
            </div>
          </div>
        )}

        <div className="row gap-3" style={{ marginTop: 8 }}>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating…' : 'Create permit'}</button>
          <Link to="/app/permits" className="btn btn-ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
